import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { Button, Table, TableRow } from '../components/ui';
import SearchableSelect from '../components/SearchableSelect';
import IconButton from '../components/IconButton';
import DateInput from '../components/DateInput';
import { useAuth } from '../context/AuthContext';

export default function Tasks() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const getUrlNumber = (name, fallback, max = Infinity) => {
    const value = Math.max(Number(searchParams.get(name)) || fallback, 1);
    return Math.min(value, max);
  };
  const page = getUrlNumber('page', 1);
  const pageSize = getUrlNumber('limit', 10, 100);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const activeTab = searchParams.get('tab') || 'Pending';
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [clientFilter, setClientFilter] = useState(searchParams.get('client_id') || '');
  const [assignedToFilter, setAssignedToFilter] = useState(searchParams.get('assigned_to') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status_id') || '');
  const [startDateFilter, setStartDateFilter] = useState(searchParams.get('start_date') || '');
  const [endDateFilter, setEndDateFilter] = useState(searchParams.get('end_date') || '');
  const [titleFilter, setTitleFilter] = useState(searchParams.get('title') || '');
  const [idFilter, setIdFilter] = useState(searchParams.get('id') || '');
  const [exporting, setExporting] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1
  });
  const { user } = useAuth();
  
  // Check if user is admin
  const isAdmin = user?.role_obj?.name?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'admin';
  
  const hasPermission = (moduleName, action) => {
    const perms = user?.permissions || {};
    const key = Object.keys(perms).find(k => k.toLowerCase() === moduleName.toLowerCase());
    return !!(key && perms[key] && perms[key][action]);
  };

  const [showTimeLogFor, setShowTimeLogFor] = useState(null); // task object
  const [timeLogForm, setTimeLogForm] = useState({ start_time: '', end_time: '', duration_hours: '', description: '' });
  const [timeLogError, setTimeLogError] = useState(null);
  const [success, setSuccess] = useState('');
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkStatusId, setBulkStatusId] = useState('');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  useEffect(() => {
    setClientFilter(searchParams.get('client_id') || '');
    setAssignedToFilter(searchParams.get('assigned_to') || '');
    setStatusFilter(searchParams.get('status_id') || '');
    setStartDateFilter(searchParams.get('start_date') || '');
    setEndDateFilter(searchParams.get('end_date') || '');
    setTitleFilter(searchParams.get('title') || '');
    setIdFilter(searchParams.get('id') || '');
  }, [location.search, searchParams]);

  useEffect(() => {
    loadTasks();
  }, [location.search, clientFilter, assignedToFilter, statusFilter, startDateFilter, endDateFilter]);

  const updateTaskListUrl = (updates = {}, resetPage = false) => {
    const nextParams = new URLSearchParams();
    const nextState = {
      tab: activeTab,
      page: resetPage ? 1 : page,
      limit: pageSize,
      client_id: clientFilter,
      assigned_to: assignedToFilter,
      status_id: statusFilter,
      start_date: startDateFilter,
      end_date: endDateFilter,
      title: titleFilter,
      id: idFilter,
      ...updates
    };

    nextParams.set('tab', nextState.tab || 'Pending');
    nextParams.set('page', String(nextState.page || 1));
    nextParams.set('limit', String(nextState.limit || 10));
    if (nextState.client_id) nextParams.set('client_id', nextState.client_id);
    if (nextState.assigned_to) nextParams.set('assigned_to', nextState.assigned_to);
    if (nextState.status_id) nextParams.set('status_id', nextState.status_id);
    if (nextState.start_date) nextParams.set('start_date', nextState.start_date);
    if (nextState.end_date) nextParams.set('end_date', nextState.end_date);
    if (nextState.title) nextParams.set('title', nextState.title);
    if (nextState.id) nextParams.set('id', nextState.id);
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    // clear bulk selection when tab changes
    setBulkSelected(new Set());
  }, [activeTab, tasks]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsRes, usersRes, statusesRes] = await Promise.all([
          api.get('/clients'),
          api.get('/users'),
          api.get('/statuses')
        ]);
        setClients(Array.isArray(clientsRes.data) ? clientsRes.data : clientsRes.data.data || []);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.data || []);
        setStatuses(Array.isArray(statusesRes.data) ? statusesRes.data : statusesRes.data.data || []);
      } catch (err) {
        console.error('Failed to load clients, users, or statuses', err);
      }
    };

    loadData();
  }, []);

  const handleExportTasks = async () => {
    try {
      setExporting(true);
      const params = {};
      
      if (clientFilter) params.client_id = clientFilter;
      if (assignedToFilter) params.assigned_to = assignedToFilter;
      if (statusFilter) params.status_id = statusFilter;
      if (startDateFilter) params.start_date = startDateFilter;
      if (endDateFilter) params.end_date = endDateFilter;
      if (titleFilter) params.title = titleFilter;
      if (idFilter) params.id = idFilter;

      const response = await api.get('/tasks/export', { 
        params,
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tasks-${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export tasks');
    } finally {
      setExporting(false);
    }
  };

  const loadTasks = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: pageSize
      };

      // pass client filter if selected
      if (clientFilter) params.client_id = clientFilter;
      
      // pass assigned_to filter if selected
      if (assignedToFilter) params.assigned_to = assignedToFilter;
      
      // pass status filter if selected
      if (statusFilter) params.status_id = statusFilter;
      
      // Add date filters
      if (startDateFilter) params.start_date = startDateFilter;
      if (endDateFilter) params.end_date = endDateFilter;
      if (titleFilter) params.title = titleFilter;
      if (idFilter) params.id = idFilter;

      // For all tasks (admin only)
      if (activeTab === 'allTasks') {
        const { data } = await api.get('/tasks/all-tasks', { params });
        setTasks(Array.isArray(data) ? data : data.data || []);
        setPagination(data.pagination || {
          total: Array.isArray(data) ? data.length : 0,
          page,
          limit: pageSize,
          totalPages: 1
        });
        return;
      }

      // For review tab we'll fetch tasks with review status and then filter by reviewer role
      if (activeTab === 'review') {
        params.status = 'review'; // Pass status parameter to backend
        if (clientFilter) params.client_id = clientFilter;
        const { data } = await api.get('/tasks', { params });
        const taskRows = Array.isArray(data) ? data : data.data || [];

        // Debugging: log counts and a sample
        console.log('Review tab fetched tasks count:', taskRows.length);

        // Include tasks where current user is the assigned reviewer (L1 or L2)
        const userId = Number(user?.id);
        const filtered = taskRows.filter(t => {
          const isL1Assigned = Number(t.l1_reviewer) === userId || Number(t.l1_reviewer_user?.id) === userId;
          const isL2Assigned = Number(t.l2_reviewer) === userId || Number(t.l2_reviewer_user?.id) === userId;

          return isL1Assigned || isL2Assigned;
        });

        console.log('Review tab filtered tasks count:', filtered.length);
        setTasks(taskRows);
        setPagination(data.pagination || {
          total: taskRows.length,
          page,
          limit: pageSize,
          totalPages: 1
        });
        return;
      }

      switch (activeTab) {
        case 'Pending':
          // open and in progress tasks assigned to the user
          params.status = 'Open';
          params.assigned_to = user.id;
          break;
        // case 'To Do':
        //   params.status = 'To Do';
        //   params.created_by = user.id;
        //   break;
        case 'review':
          // open and in progress tasks assigned to the user
          params.status = 'review';
          params.assigned_to = user.id;
          break;
        case 'mine':
          params.status = 'Done';
          params.assigned_to = user.id;
          break;
      }
      
      if (clientFilter) params.client_id = clientFilter;
      const { data } = await api.get('/tasks', { params });
      const taskRows = Array.isArray(data) ? data : data.data || [];
      setTasks(taskRows);
      setPagination(data.pagination || {
        total: taskRows.length,
        page,
        limit: pageSize,
        totalPages: 1
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh tasks after any changes
  const refreshTasks = async () => {
    await loadTasks();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }
    try {
      setError('');
      await api.delete(`/tasks/${id}`);
      setSuccess('Task deleted successfully');
      await loadTasks();
    } catch (err) {
      setSuccess('');
      setError(err.response?.data?.error || 'Failed to delete task');
    }
  };

  const handleEdit = (task) => {
    navigate(`/tasks/edit/${task.id}`);
  };

  const tabs = [
    { id: 'Pending', label: 'Open Tasks' },
    { id: 'review', label: 'Review Pending' },
    { id: 'mine', label: 'Done by Me' }
  ];

  // Add All Tasks tab for admin users
  if (isAdmin) {
    tabs.push({ id: 'allTasks', label: 'All Tasks' });
  }

  if (loading) return <div>Loading...</div>;

  // compute table headers once so we can inject a header checkbox for review tab
  const baseHeaders = isAdmin ? ['W.Id', 'Category', 'Title', 'Client', 'Status', 'Assigned To', 'Start Date', 'End Date', 'Paid', 'Fee', 'Actions'] : ['Id', 'Category', 'Title', 'Client', 'Status', 'Assigned To', 'StartDate', 'EndDate', 'Paid', 'Actions'];
  const headerCheckbox = activeTab === 'review' ? (
    <input type="checkbox" onChange={() => {
      if (bulkSelected.size === tasks.length) {
        setBulkSelected(new Set());
      } else {
        setBulkSelected(new Set(tasks.map(t => t.id)));
      }
    }} checked={tasks.length > 0 && bulkSelected.size === tasks.length} />
  ) : null;
  const tableHeaders = baseHeaders;
  const totalTasks = pagination.total || 0;
  const totalPages = pagination.totalPages || 1;
  const pageStart = totalTasks === 0 ? 0 : ((pagination.page - 1) * pagination.limit) + 1;
  const pageEnd = Math.min(pagination.page * pagination.limit, totalTasks);

  const getAllowedStatusesForTask = (task) => {
    const userId = Number(user?.id);
    const roleName = (user?.role_obj?.name || user?.role || '').toLowerCase();
    const isAdmin = roleName === 'admin';
    const isAssignee = Number(task.assigned_to) === userId || Number(task.assigned_to_user?.id) === userId;
    const isL1Reviewer = Number(task.l1_reviewer) === userId || Number(task.l1_reviewer_user?.id) === userId;
    const isL2Reviewer = Number(task.l2_reviewer) === userId || Number(task.l2_reviewer_user?.id) === userId;
    const currentStatusName = (task.Status?.name || '').toLowerCase();
    const currentStatusId = task.status_id ?? task.Status?.id;

    const allowedStatuses = statuses.filter((status) => {
      const statusName = (status.name || '').toLowerCase();
      if (isAdmin) return true;
      if (isL1Reviewer) return statusName.includes('l2 - review pending');
      if (isL2Reviewer) {
        if (currentStatusName.includes('cancel - approval pending')) {
          return statusName.includes('l2 - approval') || statusName.includes('cancelled');
        }
        return statusName.includes('l2 - approval') || statusName === 'done' || statusName.includes('cancelled');
      }
      if (isAssignee) {
        return statusName.includes('to do') ||
          statusName.includes('inprogress') ||
          statusName.includes('minutes to task - conversion pending') ||
          (statusName.includes('l1') && (statusName.includes('review') || statusName.includes('approval')));
      }
      return false;
    });

    if (currentStatusId && !allowedStatuses.some(status => Number(status.id) === Number(currentStatusId))) {
      const currentStatus = statuses.find(status => Number(status.id) === Number(currentStatusId));
      if (currentStatus) {
        allowedStatuses.unshift(currentStatus);
      }
    }

    return allowedStatuses;
  };

  const handleStatusChange = async (task, statusId) => {
    const nextStatusId = Number(statusId);
    const currentStatusId = Number(task.status_id ?? task.Status?.id);
    if (!nextStatusId || nextStatusId === currentStatusId) return;

    try {
      setUpdatingStatusId(task.id);
      const response = await api.patch(`/tasks/${task.id}/status`, { status_id: nextStatusId });
      const updatedTask = response.data;

      setTasks(prevTasks => prevTasks.map(item => {
        if (item.id !== task.id) return item;
        return {
          ...item,
          status_id: updatedTask.status_id ?? nextStatusId,
          completed_at: updatedTask.completed_at ?? item.completed_at,
          Status: updatedTask.Status || item.Status,
          updated_at: updatedTask.updated_at || item.updated_at
        };
      }));
      setSuccess('Status updated');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update task status');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  return (
    <div className="tasks-page">
      <div className="header">
        <div className="header-content">
          <div className="title-section">
            <h2>Tasks</h2>
          </div>
          
        </div>
      </div>
      <div className="tasks-header-row">
        <div className="tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => updateTaskListUrl({ tab: tab.id }, true)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/* Bulk and Add Task buttons aligned to right */}
        {hasPermission('Task', 'create') && (
          <div className="button-group">
            <input
              type="file"
              accept=".xlsx,.xls"
              id="task-upload-input"
              className="task-upload-input"
              onChange={async (e) => {
                const file = e.target.files && e.target.files[0];
                if (!file) return;
                setError('');
                setSuccess('');
                setLoading(true);
                try {
                  const form = new FormData();
                  form.append('file', file);
                  const res = await api.post('/tasks/bulk-upload', form);
                  const created = res.data?.created || 0;
                  const errors = res.data?.errors || [];
                  if (errors.length) {
                    setError(`Created ${created}. ${errors.length} rows had errors.`);
                    setError(errors.map(err => `Row ${err.row}: ${err.error}`).join('\n'));
                  } else {
                    setSuccess('Bulk upload successful. Created ' + created + ' tasks.');
                  }
                  await loadTasks();
                } catch (err) {
                  setError(err.response?.data?.error || err.message || 'Bulk upload failed');
                } finally {
                  setLoading(false);
                  e.target.value = '';
                }
              }}
            />
            
            {/* <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}> */}
              <Button 
                onClick={() => document.getElementById('task-upload-input').click()}
                className="upload-button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                </svg>
                Bulk
              </Button>
            {/* </div> */}
            <div style={{ marginLeft: 6, alignSelf: 'center' }}>
              <a href="/tasks-template.xlsx" download className="template-download-link">Sample template</a>
            </div>
            
            {/* Export Button */}
            <Button
              onClick={handleExportTasks}
              disabled={exporting}
              className="export-button"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
              {exporting ? 'Exporting...' : 'Export'}
            </Button>

            <Button 
              onClick={() => navigate('/tasks/add')}
              className="add-button add-action-button"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2"/>
              </svg>
                Task
            </Button>
          </div>
        )}
      </div>

      {/* Filters Row: Client, Assigned To, Start Date, End Date, Export */}
      <div className="filters-row">

        {/* add a filter for title and id */}
        <div className="filter-input-wrapper">
          <label>Title</label>
          <input
            type="text"
            value={titleFilter}
            onChange={e => setTitleFilter(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') updateTaskListUrl({ title: e.target.value }, true);
            }}
            placeholder="Title... (press Enter)"
          />
        </div>
        <div className="filter-input-wrapper">
          <label>ID</label>
          <input
            type="text"
            value={idFilter}
            onChange={e => setIdFilter(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') updateTaskListUrl({ id: e.target.value }, true);
            }}
            placeholder="ID... (press Enter)"
          />
        </div>

        {/* Client filter */}
        <div className="filter-select-wrapper">
          <SearchableSelect
            label="Client"
            value={clientFilter}
            options={clients.map(c => ({ value: c.id, label: (c.name + ' (' + c.company + ')') }))}
            onChange={e => {
              setClientFilter(e.target.value);
              updateTaskListUrl({ client_id: e.target.value }, true);
            }}
            placeholder="Select client"
            isClearable={true}
          />
        </div>

        {/* Assigned To filter */}
        { isAdmin &&
          <div className="filter-select-wrapper">
            <SearchableSelect
              label="Assigned To"
              value={assignedToFilter}
              options={users.map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
              onChange={e => {
                setAssignedToFilter(e.target.value);
                updateTaskListUrl({ assigned_to: e.target.value }, true);
              }}
              placeholder="Select assignee"
              isClearable={true}
            />
          </div>
      }

        {/* Status filter */}
        { isAdmin &&
          <div className="filter-select-wrapper">
            <SearchableSelect
              label="Status"
              value={statusFilter}
              options={statuses.map(s => ({ value: s.id, label: s.name }))}
              onChange={e => {
                setStatusFilter(e.target.value);
                updateTaskListUrl({ status_id: e.target.value }, true);
              }}
              placeholder="Select status"
              isClearable={true}
            />
          </div>
      }  

        {/* Start Date Filter */}
        <div className="filter-date-wrapper">
          <DateInput
            label="From"
            value={startDateFilter}
            onChange={val => {
              setStartDateFilter(val);
              updateTaskListUrl({ start_date: val }, true);
            }}
          />
        </div>

        {/* End Date Filter */}
        <div className="filter-date-wrapper">
          <DateInput
            label="To"
            value={endDateFilter}
            onChange={val => {
              setEndDateFilter(val);
              updateTaskListUrl({ end_date: val }, true);
            }}
          />
        </div>

        
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      {success && (
        <div className="success-banner">
          {success}
        </div>
      )}

      <div className="tasks-container table-responsive">
        {/* Table headers match rendered columns: admin has Fee, non-admin doesn't */}
        {/* pass checkbox as extraHeader only in review so columns align */}
        <Table headers={tableHeaders} extraHeader={headerCheckbox}>
          {tasks.map(task => {
            const status = (task.Status?.name || '').toLowerCase();
            const isDoneOrCancelled = status === 'done' || status === 'cancelled' || status === 'cancel';
            let isOverdue = false;
            if (task.end_date) {
              const ed = new Date(task.end_date);
              const endDateOnly = new Date(ed.getFullYear(), ed.getMonth(), ed.getDate());
              const now = new Date();
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
              if (endDateOnly < today) isOverdue = true;
            }
            var rowClass = '';
            if (isDoneOrCancelled) {
              rowClass = 'row-done';
            } else if ((!isDoneOrCancelled && isOverdue)) {
              rowClass = 'row-overdue';
            }

            return (
              <TableRow key={task.id} className={rowClass}>
                {activeTab === 'review' && (
                  <td style={{ width: 40, alignItems: 'center' }}>
                    <input type="checkbox" checked={bulkSelected.has(task.id)} onChange={() => {
                      setBulkSelected(prev => {
                        const s = new Set(prev);
                        if (s.has(task.id)) s.delete(task.id); else s.add(task.id);
                        return s;
                      });
                    }} />
                  </td>
                )}
                <td className="task-id" title={`${task.type}-${task.id}`}>{task.type}-{task.id}</td>
                <td className="task-category" title={task.category || '-'}>{task.category || '-'}</td>
                <td className="task-service" title={task.title || '-'}>{task.title}</td>
                <td className="task-client" title={task.Client?.company || '-'}>
                  <a href={`/clients/${task.Client?.id}/payments`} >{task.Client?.company}</a>
                </td>
                <td className={`task-status text-center status-${(task.Status?.name || '').toLowerCase().replace(/ /g, '-')}`} title={task.Status?.name}>
                  <select
                    value={task.status_id ?? task.Status?.id ?? ''}
                    onChange={(e) => handleStatusChange(task, e.target.value)}
                    disabled={updatingStatusId === task.id}
                    className="status-select"
                  >
                    {getAllowedStatusesForTask(task).map((status) => (
                      <option key={status.id} value={status.id}>{status.name}</option>
                    ))}
                  </select>
                </td>
                <td className="task-assignee" title={(task.assigned_to_user && ((task.assigned_to_user.firstName || '') + ' ' + (task.assigned_to_user.lastName || '')).trim()) || '-'}>{task.assigned_to_user?.firstName} {task.assigned_to_user?.lastName}</td>
                <td className="task-start-date" title={task.start_date ? new Date(task.start_date).toLocaleString() : '-'}>
                  {task.start_date ? new Date(task.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '-'}
                </td>
                <td className="task-end-date" title={task.end_date ? new Date(task.end_date).toLocaleString() : '-'}>
                  {task.end_date ? new Date(task.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : '-'}
                </td>
                <td className="task-paid">{task.is_payment_done ? 'Yes' : 'No'}</td>
                {isAdmin && (
                  <td className="task-fee">₹ {task.fee?.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '-'}</td>
                )}
                <td className="task-actions">
                  <div>
                    <IconButton title="Edit task" onClick={() => navigate(`/tasks/edit/${task.id}`, { state: { returnTo: `${location.pathname}${location.search}` } })}>
                      <svg viewBox="0 0 24 24" fill="none"><path d="M3 21v-3.6l11-11L20.6 9 9.6 20H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </IconButton>
                    <IconButton title="Add time log" onClick={() => {
                        setShowTimeLogFor(task);
                        setTimeLogForm({ start_time: '', end_time: '', duration_hours: '', description: '' });
                        setTimeLogError(null);
                      }}>
                      <svg viewBox="0 0 24 24" fill="none"><path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </IconButton>
                    {hasPermission('Task', 'delete') && (
                      <IconButton title="Delete task" onClick={() => handleDelete(task.id)}>
                        <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </IconButton>
                    )}
                  </div>
                </td>
              </TableRow>
            );
          })}
        </Table>

        <div className="pagination-bar">
          <div className="pagination-summary">
            Showing {pageStart}-{pageEnd} of {totalTasks} tasks
          </div>
          <div className="pagination-controls">
            <label className="pagination-size">
              Rows
              <select
                value={pageSize}
                onChange={e => updateTaskListUrl({ limit: Number(e.target.value) }, true)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
            <button
              type="button"
              className="pagination-button"
              onClick={() => updateTaskListUrl({ page: Math.max(page - 1, 1) })}
              disabled={pagination.page <= 1}
            >
              Prev
            </button>
            <label className="pagination-page">
              Page
              <input
                type="number"
                min="1"
                max={totalPages}
                value={pagination.page}
                onChange={e => {
                  const nextPage = Number(e.target.value);
                  if (Number.isFinite(nextPage)) {
                    updateTaskListUrl({ page: Math.min(Math.max(nextPage, 1), totalPages) });
                  }
                }}
              />
              <span>of {totalPages}</span>
            </label>
            <button
              type="button"
              className="pagination-button"
              onClick={() => updateTaskListUrl({ page: Math.min(page + 1, totalPages) })}
              disabled={pagination.page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>

        {activeTab === 'review' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
            <button className="generate-btn" disabled={bulkSelected.size===0} onClick={async () => {
              if (bulkSelected.size === 0) return;
              if (!window.confirm(`Update ${bulkSelected.size} selected tasks?`)) return;
              try {
                const ids = Array.from(bulkSelected);

                const statusMapping = {"2": 3, "3": 4, "6": 7, "7": 8}; // if current status is review L1 (2) move to review L2 (3), if review L2 (3) move to Done (4)
                const payload = ids.map(id => {
                  const t = tasks.find(x => x.id === id) || {};
                  return { id, current_status_id: t.status_id ?? t.Status?.id ?? null, target_status_id: statusMapping[t.status_id ?? t.Status?.id] };
                });
                await api.post('/tasks/bulk-update-status', { tasks: payload });
                setSuccess('Tasks updated');
                setBulkSelected(new Set());
                await loadTasks();
              } catch (err) {
                setError(err.response?.data?.error || 'Failed to update tasks');
              }
            }}>Update Bulk Status</button>
          </div>
        )}

        {/* Time Log Modal */}
        {showTimeLogFor && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Add Time Log — {showTimeLogFor.title}</h3>
              {timeLogError && <div className="time-log-error">{timeLogError}</div>}
              <form onSubmit={async (e) => {
                e.preventDefault();
                setTimeLogError(null);
                const { start_time, end_time, description } = timeLogForm;
                if (!start_time || !end_time) { setTimeLogError('Start and end time are required'); return; }
                const start = new Date(start_time);
                const end = new Date(end_time);
                if (end <= start) { setTimeLogError('End time must be after start time'); return; }
                const durationHours = (end - start) / (1000 * 60 * 60);
                try {
                  await api.post(`/tasks/${showTimeLogFor.id}/time-logs`, { duration_hours: durationHours.toFixed(2), start_time, end_time, description });
                  setShowTimeLogFor(null);
                  setTimeLogForm({ start_time: '', end_time: '', duration_hours: '', description: '' });
                  await loadTasks();
                  alert('Time log added');
                } catch (err) {
                  setTimeLogError(err.response?.data?.error || 'Failed to add time log');
                }
              }}>
                <div className="time-log-row">
                  <label className="time-log-label">From:</label>
                  <input type="datetime-local" value={timeLogForm.start_time} onChange={e => {
                    const val = e.target.value;
                    setTimeLogForm(f => ({ ...f, start_time: val, duration_hours: (f.end_time && val) ? (((new Date(f.end_time) - new Date(val)) / (1000*60*60)).toFixed(2)) : f.duration_hours }));
                  }} />
                  <label className="time-log-label">To:</label>
                  <input type="datetime-local" value={timeLogForm.end_time} onChange={e => {
                    const val = e.target.value;
                    setTimeLogForm(f => ({ ...f, end_time: val, duration_hours: (f.start_time && val) ? (((new Date(val) - new Date(f.start_time)) / (1000*60*60)).toFixed(2)) : f.duration_hours }));
                  }} />
                </div>
                <div className="time-log-group">
                  <label className="time-log-label">Duration (hours):</label>
                  <input type="text" readOnly value={timeLogForm.duration_hours} />
                </div>
                <div className="time-log-group">
                  <label className="time-log-label">Notes / Description:</label>
                  <textarea value={timeLogForm.description} onChange={e => setTimeLogForm(f => ({ ...f, description: e.target.value }))} className="time-log-textarea" />
                </div>
                <div className="time-log-actions">
                  <button type="button" className="back-btn" onClick={() => setShowTimeLogFor(null)}>Cancel</button>
                  <button type="submit" className="generate-btn">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .tasks-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .tasks-header-row {
          display: flex;
          gap: 12px;
          align-items: center;
          padding: 0 16px;
          margin-bottom: 16px;
          justify-content: space-between;
          flex-wrap: wrap;
        }

        .button-group {
          display: flex;
          gap: 12px;
        }

        .task-upload-input {
          display: none;
        }

        .filters-row {
          display: flex;
          gap: 12px;
          align-items: center;
          padding: 0 16px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .filter-input-wrapper,
        .filter-select-wrapper,
        .filter-date-wrapper {
          width: 190px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .filter-date-wrapper {
          align-items: stretch;
        }

        .filter-input-wrapper label,
        .filter-select-wrapper label,
        .filter-date-wrapper label {
          color: #6B778C;
          font-size: 13px;
          margin-bottom: 2px;
        }

        .filter-input-wrapper input,
        .filter-date-wrapper .date-input,
        .filter-select-wrapper .react-select__control {
          width: 100%;
          border-radius: 8px;
          border: 1px solid #e1e4e8;
          box-shadow: none;
          min-height: 42px;
          background: white;
        }

        .filter-input-wrapper input:focus,
        .filter-date-wrapper .date-input:focus,
        .filter-select-wrapper .react-select__control--is-focused {
          border-color: #0066cc;
          box-shadow: 0 0 0 4px rgba(0,102,204,0.08);
          outline: none;
        }

        .filter-label {
          font-size: 14px;
          color: #6B778C;
          white-space: nowrap;
        }

        .filter-input {
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
        }

        .template-download-link {
          display: inline-block;
          color: #0057b7;
          text-decoration: underline;
          font-size: 12px;
          margin-top: 17px;
          position: absolute;
          margin-left: -95px;
        }

        .template-download-link:hover {
          color: #003a7a;
        }

        .export-button {
          padding: 8px 16px;
          background-color: #2e7d32;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          opacity: 1;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: opacity 0.2s;
        }

        .export-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .export-button:hover:not(:disabled) {
          opacity: 0.9;
        }

        .time-log-row {
          display: flex;
          gap: 8px;
          margin-bottom: 8px;
          flex-wrap: wrap;
          align-items: center;
        }

        .time-log-label {
          font-size: 12px;
          white-space: nowrap;
        }

        .time-log-group {
          margin-bottom: 8px;
        }

        .time-log-textarea {
          width: 100%;
          min-height: 80px;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 14px;
        }

        .time-log-actions {
          display: flex;
          gap: 8px;
          justify-content: flex-end;
        }

        .time-log-error {
          color: #de350b;
          margin-bottom: 12px;
        }

        .success-banner {
          background: #E3FCEF;
          color: #006644;
          padding: 12px 16px;
          border-radius: 3px;
          margin-bottom: 20px;
          text-align: center;
        }

        .title-section {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .title-section h2 {
          margin: 0;
          color: #172B4D;
          font-size: 24px;
        }

        .tabs {
          display: flex;
          gap: 8px;
          border-bottom: 1px solid #DFE1E6;
          padding-bottom: 1px;
        }

       

        .error-banner {
          background: #FFEBE6;
          color: #DE350B;
          padding: 12px 16px;
          border-radius: 3px;
          margin-bottom: 20px;
          text-align: center;
        }

        .tasks-container {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          padding: 16px;
        }

        .task-title {
          font-weight: 500;
          color: #172B4D;
          width: 240px;
          min-width: 240px;
          max-width: 240px;
        }

        .task-service {
          color: #42526E;
          width: 240px;
          min-width: 240px;
          max-width: 240px;
        }

        .task-status {
          display: inline-block;
          padding: 4px 8px;
          font-size: 12px;
          font-weight: 500;
          color: #fff;
          border-radius: 5px;
          margin: 5px 0px;
        }
        
        .status-l1---approval-pending,  .status-l2---approval-pending{
          background: #7e9df3ff;
          color: #fff;
        }
        .status-inprogress{
          background: #516cceff;
        }  
        .status-l1---review-pending, .status-l2---review-pending, .status-cancel---approval-pending{
          background: #f99530ff;
         
        }
        .status-done{
          background: #7ed957ff;
         
        }
        .status-cancelled{
          background: #b00a0aff;
        }
        .status-to-do{
          background: #e24c7eff;
        }      
        
        .status-backlog{
          background: #8c65b4ff;
        }
        .status-follow-up{
          background: #8c65b4ff;
        }      
        .status-open {
          background: #E3FCEF;
          color: #006644;
        }

        .status-pending {
          background: #FFFAE6;
          color: #172B4D;
        }
        .row-done
        {          
          background: #94f1c1ff;
          // color: #fff;
        }
        .row-overdue {
          background: #ff8e7aff;
          // color: #fff;
        }
        .task-assignee {
          color: #42526E;
        }

        .task-actions {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .task-actions .icon-button {
          width: 34px;
          height: 34px;
          padding: 4px;
          border-radius: 6px;
          background: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .task-actions .icon-button:hover { background: #f4f5f7; }

        /* Responsive Table Styling */
        .tasks-container table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .tasks-container table th,
        .tasks-container table td {
          border-bottom: 1px solid #e0e0e0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          height: 48px;
          vertical-align: middle;
          font-size: 14px;
        }

        .tasks-container table th {
          background: linear-gradient(135deg, #f8f9fa 0%, #f0f1f3 100%);
          font-weight: 600;
          color: #333;
          position: sticky;
          top: 0;
          z-index: 10;
          font-size: 13px;
        }

        .tasks-container table tbody tr:hover {
          background-color: rgba(0, 102, 204, 0.04);
        }

        .pagination-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding-top: 14px;
          flex-wrap: wrap;
        }

        .pagination-summary {
          color: #5E6C84;
          font-size: 13px;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .pagination-size,
        .pagination-page {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #42526E;
          font-size: 13px;
        }

        .pagination-size select,
        .pagination-page input {
          height: 34px;
          border: 1px solid #dfe1e6;
          border-radius: 6px;
          padding: 0 8px;
          background: white;
          color: #172B4D;
        }

        .pagination-page input {
          width: 64px;
        }

        .pagination-button {
          height: 34px;
          padding: 0 12px;
          border: 1px solid #dfe1e6;
          border-radius: 6px;
          background: #fff;
          color: #172B4D;
          cursor: pointer;
        }

        .pagination-button:hover:not(:disabled) {
          background: #f4f5f7;
        }

        .pagination-button:disabled {
          color: #97A0AF;
          cursor: not-allowed;
          background: #f4f5f7;
        }

        /* Highlight critical rows (done/cancelled or overdue) */
        .row-critical td {
          background: #ffecec;
          color: #8b0000;
        }
        .row-critical .task-status {
          background: transparent !important;
          color: #8b0000 !important;
          font-weight: 600;
        }

        /* Column Classes for width management */
        .task-id { width: 8%; min-width: 70px; }
        /* .task-service { width: 240px; min-width: 240px; max-width: 240px; } */
       /* .task-client { width: 12%; min-width: 100px; }*/
        .task-status { width: 10%; min-width: 85px; }
        .task-assignee { width: 12%; min-width: 100px; }
       /* .task-start-date { width: 10%; min-width: 85px; } */
       /* .task-end-date { width: 10%; min-width: 85px; } */
        .task-paid { width: 8%; min-width: 60px; }
        .task-fee { width: 9%; min-width: 75px; }
        .task-actions { width: 15%; min-width: 100px; }

        /* Tablet View - Adjust column widths */
        @media (max-width: 1024px) {
          .tasks-container table th,
          .tasks-container table td {
            padding: 10px 8px;
            font-size: 13px;
          }

          .task-id { width: 8%; min-width: 65px; }
          /* .task-service { width: 220px; min-width: 220px; max-width: 220px; } */
          /* .task-client { width: 13%; min-width: 90px; } */
          .task-status { width: 11%; min-width: 80px; }
          .task-assignee { width: 12%; min-width: 90px; }
          .task-start-date { width: 11%; min-width: 80px; }
          .task-end-date { width: 11%; min-width: 80px; }
          .task-paid { width: 8%; min-width: 55px; }
          .task-fee { width: 9%; min-width: 70px; }
          .task-actions { width: 15%; min-width: 85px; }
        }

        /* Mobile View - Stack and adjust */
        @media (max-width: 768px) {
          .tasks-container {
            padding: 12px;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .tasks-container table {
            table-layout: auto;
            min-width: 700px;
          }

          .tasks-container table th,
          .tasks-container table td {
            padding: 8px 6px;
            font-size: 12px;
          }

          .task-id { width: 8%; min-width: 60px; }
          /* .task-service { width: 200px; min-width: 200px; max-width: 200px; } 
          .task-client { width: 14%; min-width: 85px; } */
          .task-status { width: 12%; min-width: 75px; }
          .task-assignee { width: 13%; min-width: 85px; }
          .task-start-date { width: 12%; min-width: 75px; }
          .task-end-date { width: 12%; min-width: 75px; }
          .task-paid { width: 8%; min-width: 50px; }
          .task-fee { width: 8%; min-width: 65px; }
          .task-actions { width: 15%; min-width: 140px; }

          .task-actions {
            gap: 4px;
          }

          .task-actions .icon-button {
            width: 32px;
            height: 32px;
            padding: 3px;
          }
        }

        /* Small Mobile - Further optimization */
        @media (max-width: 480px) {
          .tasks-container table {
            min-width: 600px;
          }

          .tasks-container table th,
          .tasks-container table td {
            padding: 6px 4px;
            font-size: 11px;
          }

          .task-id { width: 8%; min-width: 55px; }
          /* .task-service { width: 180px; min-width: 180px; max-width: 180px; } */
          /* .task-client { width: 15%; min-width: 80px; } */
          .task-status { width: 12%; min-width: 70px; }
          .task-assignee { width: 13%; min-width: 80px; }
          .task-start-date { width: 13%; min-width: 70px; }
          .task-end-date { width: 13%; min-width: 70px; }
          .task-paid { width: 8%; min-width: 45px; }
          .task-fee { width: 8%; min-width: 60px; }
          .task-actions { width: 15%; min-width: 135px; }

          .task-actions .icon-button {
            width: 30px;
            height: 30px;
            padding: 2px;
          }
        }

        /* Modal styles */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: #fff;
          padding: 16px;
          border-radius: 8px;
          width: 640px;
          max-width: 95%;
        }
        .modal h3 { margin: 0 0 12px 0; }
        .generate-btn { background:#0057b7; color:#fff; padding:8px 10px; border-radius:6px; border:none; cursor:pointer; }
        .back-btn { padding:8px 10px; border-radius:6px; background:#f4f5f7; border:none; cursor:pointer }      `}</style>
    </div>
  );
}
